import React, {useState, useEffect} from "react"
import {
	Button,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	IconButton,
	Box,
	Typography,
	Tooltip,
	Grid,
} from "@mui/material"

import CloseIcon from "@mui/icons-material/Close"
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined"
import AlertWithStatus from "../../../../feedback/AlertWithStatus"
import {makeStyles} from "tss-react/mui"
import CircularProgress from "@mui/material/CircularProgress"
import OrderService from "../../../../../services/order/OrderService"
import {postActionLog} from "../../../../../services/order/TrackOrderActions"
import {ButtonActionTypes} from "../../../../../services/order/TrackOrderActions"
import {ModifiedEvent} from "../types"
import PreviewPanel from "./PreviewPanel"
import ThumbnailItem from "./ThumbnailItem"
import {LineItem} from "../../../../../dtos/orderResponse"
import {getCaseId} from "../../../../../utils/UrlParser"

const caseId = getCaseId()
interface CompareModalProps {
	open: boolean
	onClose: () => void
	events: ModifiedEvent[]
	orderNumber: string
	lineItem: LineItem
	reloadParent: () => void
	latestVersion: ModifiedEvent | null
	documentId: string | null
	locale: string
	productKey: string
	optionSelection: Object
	previewInstructionsUri: string
}
interface AlertOptions {
	open: boolean
	message?: string
	type?: string
}
const defaultAlert: AlertOptions = {
	open: false,
	message: "",
	type: "err",
}
const useStyle = makeStyles()({
	previewImage: {width: "100%", height: "auto", objectFit: "contain"},
	thumbnailImage: {width: "100%", height: "auto", objectFit: "contain", display: "block"},

	loading: {
		margin: "auto",
		paddingBottom: "10px",
	},
	buttons: {
		paddingBottom: "10px",
		paddingRight: "10px",
	},
})

const CompareModal: React.FC<CompareModalProps> = ({
	open,
	onClose,
	events,
	orderNumber,
	lineItem,
	reloadParent,
	latestVersion,
	documentId,
	locale,
	productKey,
	optionSelection,
	previewInstructionsUri,
}) => {
	const {classes} = useStyle()
	const [alertOptions, setAlertOptions] = useState(defaultAlert)
	const [selectedVersion, setSelectedVersion] = useState<ModifiedEvent | null>(null)
	const [isReplaced, setIsReplaced] = useState(false)
	const [isSubmitting, setIsSubmitting] = useState(false)
	const [startTime, setStartTime] = useState(new Date().getTime())

	const handleClose = () => {
		postActionLog(
			{
				event: ButtonActionTypes.ClickReplacePreviousDocumentRevisionCancelButton,
				actionMetaData: {
					orderNumber: orderNumber,
					lineItemId: lineItem.id,
					workId: documentId,
				},
			},
			caseId
		)
		setSelectedVersion(null)
		setIsReplaced(false)
		onClose()
	}

	const handleSelectVersion = (item: ModifiedEvent) => {
		setSelectedVersion(item)
		setIsReplaced(false)
	}

	const handleReplaceCurrent = () => {
		postActionLog(
			{
				event: ButtonActionTypes.ClickReplacePreviousDocumentRevisionReplaceCurrentButton,
				actionMetaData: {
					orderNumber: orderNumber,
					lineItemId: lineItem.id,
					workId: documentId,
				},
			},
			caseId
		)
		setIsReplaced(true)
	}

	const handleResetToOriginal = () => {
		postActionLog(
			{
				event: ButtonActionTypes.ClickReplacePreviousDocumentRevisionResetToOriginalButton,
				actionMetaData: {
					orderNumber: orderNumber,
					lineItemId: lineItem.id,
					workId: documentId,
				},
			},
			caseId
		)
		setSelectedVersion(null)
		setIsReplaced(false)
	}
	useEffect(() => {
		setStartTime(new Date().getTime())
	}, [open])
	function getAlertOptions(results: any) {
		const {status} = results
		if (status === 207) {
			return {
				...alertOptions,
				type: "wrn",
				message: `Document previous revision replaced successfully, but document update failed. Please proceed with the update document action for line item manually`,
				open: true,
			}
		} else if (status === 200) {
			return {
				...alertOptions,
				type: "scs",
				message: `Document previous revision replaced and updated successfully`,
				open: true,
			}
		} else
			return {
				...alertOptions,
				type: "err",
				message: "Replacement of previous document revision failed",
				open: true,
			}
	}
	const handleSubmit = async () => {
		try {
			setIsSubmitting(true)
			if (selectedVersion) {
				const orderService = new OrderService()
				const elapsedTime = new Date().getTime() - startTime
				const elapsedSeconds = elapsedTime / 1000

				const results = await orderService.replaceLineItemDocumentPreviousRevision(
					orderNumber,
					lineItem.id,
					selectedVersion?.workId,
					selectedVersion?.workRevisionId,
					selectedVersion.shopperId,
					elapsedSeconds
				)
				setAlertOptions(getAlertOptions(results))
			}
		} catch (err) {
			console.error("Error replacing line item document revision:", err)
			setAlertOptions({
				open: true,
				type: "err",
				message: "Replacement of previous document revision failed",
			})
		} finally {
			setIsSubmitting(false)
			setIsReplaced(false)
			handleClose()
			reloadParent()
		}
	}

	const previewProps = {
		locale,
		productKey,
		optionSelection,
		previewInstructionsUri,
		previewImageClass: classes.previewImage,
	}

	return (
		<div>
			<Dialog open={open} onClose={handleClose} fullWidth maxWidth="xl">
				<DialogTitle>
					<Typography variant="h6">Compare Documents</Typography>
					<IconButton
						aria-label="close"
						onClick={handleClose}
						sx={{position: "absolute", right: 8, top: 8, color: (theme) => theme.palette.grey[500]}}
					>
						<CloseIcon />
					</IconButton>
				</DialogTitle>

				<DialogContent dividers>
					<Box sx={{display: "flex", gap: 3, alignItems: "stretch", height: "100%"}}>
						<Box
							sx={{
								flex: 1,
								minWidth: 0,
								display: "flex",
								flexDirection: "column",
							}}
						>
							<Box sx={{flex: 1}}>
								<Typography variant="subtitle1" sx={{mb: 1, fontWeight: 500}}>
									Current Version
								</Typography>

								<PreviewPanel
									{...previewProps}
									event={latestVersion}
									placeholder="No current version available"
								/>
							</Box>

							<Box sx={{mt: 1}}>
								<Button
									variant="contained"
									size="small"
									onClick={handleResetToOriginal}
									disabled={!isReplaced}
								>
									Reset to the original
								</Button>
							</Box>
						</Box>

						<Box
							sx={{
								flex: 1,
								minWidth: 0,
								display: "flex",
								flexDirection: "column",
							}}
						>
							<Box sx={{flex: 1}}>
								<Typography variant="subtitle1" sx={{mb: 1, fontWeight: 500}}>
									Selected Version
								</Typography>

								<PreviewPanel
									{...previewProps}
									event={selectedVersion}
									placeholder="Select a version from the right to replace current version"
								/>
								<Box sx={{minHeight: 40, mt: 1, display: "flex", alignItems: "center"}}>
									{selectedVersion && (
										<>
											<Typography variant="body2">
												Date of modification: {new Date(selectedVersion.date).toLocaleString()}
											</Typography>

											<Tooltip title={`Changed by: ${selectedVersion.owner}`}>
												<InfoOutlinedIcon
													fontSize="small"
													sx={{ml: 1, cursor: "pointer", color: "#555"}}
												/>
											</Tooltip>
										</>
									)}
								</Box>
							</Box>

							<Box sx={{mt: 1}}>
								<Button
									variant="contained"
									size="small"
									onClick={handleReplaceCurrent}
									disabled={!selectedVersion || isReplaced}
								>
									Replace Current
								</Button>
							</Box>
						</Box>

						<Box sx={{width: 220, flexShrink: 0}}>
							<Typography variant="subtitle1" sx={{mb: 1, fontWeight: 500}}>
								Previous versions
							</Typography>
							<Box
								sx={{
									border: "1px solid #ccc",
									borderRadius: 1,
									maxHeight: 450,
									overflowY: "auto",
									p: 1,
									display: "flex",
									flexDirection: "column",
									gap: 1,
								}}
							>
								{events.map((item, index) => (
									<ThumbnailItem
										key={index}
										item={item}
										isSelected={selectedVersion === item}
										onClick={handleSelectVersion}
										thumbnailImageClass={classes.thumbnailImage}
									/>
								))}
							</Box>
						</Box>
					</Box>
				</DialogContent>

				<DialogActions sx={{px: 3, pb: 2}}>
					{isSubmitting ? (
						<CircularProgress size={16} color="inherit" />
					) : (
						<Grid className={classes.buttons} container spacing={2} justifyContent="flex-end">
							<Grid item>
								<Button variant="outlined" onClick={handleClose}>
									Cancel
								</Button>
							</Grid>
							<Grid item>
								<Button
									variant="contained"
									disabled={!isReplaced || isSubmitting}
									onClick={handleSubmit}
								>
									Submit
								</Button>
							</Grid>
						</Grid>
					)}
				</DialogActions>
			</Dialog>
			{alertOptions.type != null && (
				<AlertWithStatus
					open={alertOptions.open}
					message={alertOptions.message}
					closeAlert={() => {
						setAlertOptions({...alertOptions, open: false})
					}}
					status={alertOptions.type}
				/>
			)}
		</div>
	)
}

export default CompareModal
